import usePureState from '@/hooks/usePureState';
import { useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { ICalendarEventMinimumData } from '../../types/ICalendarEvent';
import { ITimeZone } from '../../types/ITimeZone';
import weekCalendarDayService, { calculatePositionAndHeight } from './week-calendar-day.service';

interface IProps<TEvent extends ICalendarEventMinimumData> {
	onEventPartialUpdated: (event: TEvent) => void;
	onEventUpdated: (event: TEvent) => void;
	onCalendarPointerDown: (start: Temporal.PlainDateTime) => void;
	onCalendarPointerMove: (start: Temporal.PlainDateTime) => void;
	onCalendarPointerUp: (start: Temporal.PlainDateTime) => void;
	onEventClick: (args: { event: React.MouseEvent; calendarEvent: TEvent }) => void;
	plainDate: Temporal.PlainDate;
	events: Array<TEvent>;
	hourHeightInPixels: number;
	timeZone: ITimeZone;
}

/**
 * Probably we don't need both phantom and edited.
 */
interface IState<TEvent> {
	/**
	 * Used to track which event is being resized or moved.
	 * This is because the pointerMove events need to know which calendar event needs to be updated.
	 */
	editedEvent: TEvent | null;
	/**
	 * If you move an event this will keep the original position.
	 */
	phantomEvent: TEvent | null;
	/**
	 * This compensates the point in space where the user interacts.
	 * ---A----
	 * |      |
	 * |  B   |
	 * |      |
	 * ---C----
	 *
	 * When the user drags a timeblock clicking B we want to move the start taking the difference into account (B-A).
	 */
	offsetY: number;
}

/**
 * Time threshold to differentiate between a click and a "long press".
 */
const LONG_CLICK_DURATION_MS = 200;

export function useWeekCalendarDayController<TEvent extends ICalendarEventMinimumData>(props: IProps<TEvent>) {
	// TODO: Unify those functions for performance
	const events = weekCalendarDayService.assignZIndexAndLeft(props.events, props.plainDate).map((e) => ({
		...e,
		...calculatePositionAndHeight(e, props.hourHeightInPixels, props.timeZone),
	}));

	const stateRef = useRef<{
		/**
		 * Used to track what is being resized on a resized event. The start or the end.
		 */
		action: 'start' | 'end' | 'move' | null;
		/**
		 * Long click detection
		 */
		longClickTimeOut: null | NodeJS.Timeout;
	}>({
		action: null,
		longClickTimeOut: null,
	});

	const { state, dispatch } = usePureState<IState<TEvent>>({
		editedEvent: null,
		offsetY: 0,
	});

	function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
		if (e.pointerType === 'touch') {
			console.log('handle pointer touch down.');
			dispatch({ editedEvent: null });
			return;
		}

		console.warn('handlePointerDown');
		const plainDateTime = weekCalendarDayService.getDateFromMouseEvent(
			e,
			state.offsetY,
			props.hourHeightInPixels,
			props.plainDate
		);
		props.onCalendarPointerDown(plainDateTime);
	}

	function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
		console.log('handlePointerUp');
		const plainDateTime = weekCalendarDayService.getDateFromMouseEvent(
			e,
			state.offsetY,
			props.hourHeightInPixels,
			props.plainDate
		);
		props.onCalendarPointerUp(plainDateTime);

		/**
		 * No changes, keep the focus on the active event.
		 */
		if (
			state.editedEvent &&
			state.phantomEvent &&
			state.editedEvent.start.equals(state.phantomEvent.start) &&
			state.editedEvent.end.equals(state.phantomEvent.end)
		) {
			return;
		}

		/**
		 * Otherwise reset the edited and the phantom
		 */
		if (state.editedEvent) {
			props.onEventUpdated(state.editedEvent);
		}

		dispatch({ editedEvent: null, phantomEvent: null });
		stateRef.current.action = null;
	}

	function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
		console.log('handlePointerMove');
		const plainDateTime = weekCalendarDayService.getDateFromMouseEvent(
			e,
			state.offsetY,
			props.hourHeightInPixels,
			props.plainDate
		);

		// Si hay un editEvent vamos a moverlo
		if (state.editedEvent && !stateRef.current.action) {
			// When moving an event we want to adjust the interaction depending on where the user interacted in the event.
			const adjustedPlainDateTime = weekCalendarDayService.getDateFromMouseEvent(
				e,
				state.offsetY,
				props.hourHeightInPixels,
				props.plainDate
			);
			const startZonedDateTime = Temporal.ZonedDateTime.from(state.editedEvent.start);
			const endZonedDateTime = Temporal.ZonedDateTime.from(state.editedEvent.end);
			const eventDuration = startZonedDateTime.until(endZonedDateTime);

			const start = adjustedPlainDateTime.toZonedDateTime(props.timeZone).round({ smallestUnit: 'minute' });

			const end = start.add(eventDuration);
			dispatch({ editedEvent: { ...state.editedEvent, start, end } });
			props.onEventPartialUpdated({ ...state.editedEvent, start, end });
		}

		// IF we are resizing something emit an event updated.
		if (state.editedEvent && stateRef.current.action) {
			e.preventDefault();

			dispatch({
				editedEvent: {
					...state.editedEvent,
					[stateRef.current.action]: plainDateTime.toZonedDateTime(props.timeZone),
				},
			});

			props.onEventPartialUpdated({
				...state.editedEvent,
				[stateRef.current.action]: plainDateTime.toZonedDateTime(props.timeZone),
			});
		}

		props.onCalendarPointerMove(plainDateTime);
	}

	function handleEventResizePointerDown(e: React.MouseEvent, event: TEvent, action: 'start' | 'end') {
		e.preventDefault();
		e.stopPropagation();

		dispatch({ editedEvent: event });

		stateRef.current = { ...stateRef.current, action };
	}

	function handleEventResizePointerUp() {
		console.log('handleEventResizePointerUp');

		dispatch({ editedEvent: null, phantomEvent: null });
		stateRef.current.action = null;
	}

	function handleEventPointerDown(e: React.PointerEvent, event: TEvent) {
		console.warn('handleEventPointerDown');
		e.preventDefault();
		e.stopPropagation();
		// Guardar el offset vertical entre el puntero y el inicio del evento
		const eventElement = e.currentTarget as HTMLElement;
		const offsetY = e.clientY - eventElement.getBoundingClientRect().top;

		stateRef.current.longClickTimeOut = setTimeout(() => {
			console.warn('long click');

			console.log({ offsetY });

			dispatch({ offsetY, phantomEvent: event, editedEvent: event });
			stateRef.current.longClickTimeOut ? clearTimeout(stateRef.current.longClickTimeOut) : () => {};
			stateRef.current.longClickTimeOut = null;
		}, LONG_CLICK_DURATION_MS);
	}

	function handleEventPointerUp(event: React.PointerEvent, calendarEvent: TEvent) {
		console.log('handleEventPointerUp');

		/**
		 * SHORT PRESS.
		 *
		 * If the pointer up is too soon there is a timeout so we need to clear it
		 * and emit an onclick event.
		 */
		if (stateRef.current.longClickTimeOut) {
			console.warn('short click');
			stateRef.current.longClickTimeOut ? clearTimeout(stateRef.current.longClickTimeOut) : () => {};
			stateRef.current.longClickTimeOut = null;
			return props.onEventClick({ event, calendarEvent });
		}

		/**
		 * LONG PRESS
		 *
		 * Otherwise this pointer up is triggered after a long press.
		 *
		 * Clear the timeout (just in case)
		 */
		stateRef.current.longClickTimeOut ? clearTimeout(stateRef.current.longClickTimeOut) : () => {};
		stateRef.current.longClickTimeOut = null;
	}

	return {
		handleEventPointerDown,
		handleEventResizePointerDown,
		handleEventResizePointerUp,
		handlePointerDown,
		handlePointerMove,
		handlePointerUp,
		handleEventPointerUp,
		events,
		state,
		stateRef,
	};
}
